import React from 'react';
import { Check, Lock } from 'lucide-react';
import type { ContractInstanceData } from '@/types/recruitment';

// Local type for onboarding progress (replaces removed CompletionStats)
interface OnboardingProgress {
  percentComplete: number;
}

interface RecruitmentProgressionBarProps {
  candidateId?: string;
  contractInstance?: ContractInstanceData;
  onboardingStats?: OnboardingProgress; // Optional until onboarding system is rebuilt
}

interface ProgressionStep {
  id: number;
  label: string;
  key: string;
}

const RECRUITMENT_STEPS: ProgressionStep[] = [
  { id: 1, label: 'Recruit Invited', key: 'invited' },
  { id: 2, label: 'LO Signed Contract', key: 'lo_signed' },
  { id: 3, label: 'Company Counter Signed', key: 'company_signed' },
  { id: 4, label: 'Onboarding Completed', key: 'onboarding_done' },
  { id: 5, label: 'Onboarding Email Sent', key: 'email_sent' }
];

type StepState = 'complete' | 'active' | 'pending' | 'locked';

export const RecruitmentProgressionBar: React.FC<RecruitmentProgressionBarProps> = ({
  candidateId,
  contractInstance,
  onboardingStats
}) => {
  /**
   * Determine completion state for each step
   */
  const getStepState = (step: ProgressionStep): StepState => {
    switch (step.key) {
      case 'invited':
        // Step 1: Always complete if candidate exists
        return candidateId ? 'complete' : 'pending';

      case 'lo_signed':
        // Step 2: Complete if LO has signed the contract
        if (contractInstance?.hasLoSignature || contractInstance?.loanOfficerSignatureDate) {
          return 'complete';
        }
        // Active if candidate invited but not signed yet
        return candidateId ? 'active' : 'pending';

      case 'company_signed':
        // Step 3: Future development - show as locked for now
        // When implemented: check contractInstance?.hasRecruiterSignature
        if (contractInstance?.hasRecruiterSignature || contractInstance?.recruiterSignatureDate) {
          return 'complete';
        }
        // Show as active if LO has signed
        if (contractInstance?.hasLoSignature || contractInstance?.loanOfficerSignatureDate) {
          return 'active';
        }
        return 'locked';

      case 'onboarding_done':
        // Step 4: Complete if onboarding is 100% done
        if (onboardingStats?.percentComplete === 100) {
          return 'complete';
        }
        // Active if company has signed
        if (contractInstance?.hasRecruiterSignature || contractInstance?.recruiterSignatureDate) {
          return 'active';
        }
        return 'pending';

      case 'email_sent':
        // Step 5: Future development - always pending for now
        // When implemented: check for email_sent timestamp
        if (onboardingStats?.percentComplete === 100) {
          return 'active';
        }
        return 'pending';

      default:
        return 'pending';
    }
  };

  /**
   * Find the current active step
   */
  const getCurrentStep = (): number => {
    for (let i = RECRUITMENT_STEPS.length - 1; i >= 0; i--) {
      const state = getStepState(RECRUITMENT_STEPS[i]);
      if (state === 'complete') {
        // Return next step if not last
        return i < RECRUITMENT_STEPS.length - 1 ? i + 2 : i + 1;
      }
      if (state === 'active') {
        return i + 1;
      }
    }
    return 1; // Default to first step
  };

  const currentStep = getCurrentStep();
  const pct = Math.round((currentStep / RECRUITMENT_STEPS.length) * 100);

  const renderCircle = (step: ProgressionStep) => {
    const state = getStepState(step);
    return (
      <div className={`stepper-circle ${state}`}>
        {state === 'complete' && <Check />}
        {state === 'active' && <span className="stepper-pulse" />}
        {state === 'locked' && <Lock />}
      </div>
    );
  };

  return (
    <div className="stepper">
      <div className="stepper-head">
        <div className="stepper-title">Recruitment Progress</div>
        <div className="stepper-count">
          Step {currentStep} of {RECRUITMENT_STEPS.length}
        </div>
      </div>

      <div className="stepper-row">
        {RECRUITMENT_STEPS.map((step, index) => (
          <React.Fragment key={step.id}>
            {renderCircle(step)}
            {index < RECRUITMENT_STEPS.length - 1 && (
              <div className={`stepper-conn${getStepState(step) === 'complete' ? ' complete' : ''}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      <div className="stepper-labels">
        {RECRUITMENT_STEPS.map((step) => (
          <div key={`label-${step.id}`} className={`stepper-label ${getStepState(step)}`}>
            {step.label}
          </div>
        ))}
      </div>

      <div className="stepper-foot">
        <div className="stepper-foot-row">
          <span>Overall progress</span>
          <b>{pct}%</b>
        </div>
        <div className="stepper-bar">
          <div className="stepper-bar-fill" style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
};
